import React, { useEffect, useRef } from 'react';

const PARTICLE_COUNT = 110;
const CONNECTION_DIST = 160;
const MOUSE_FORCE_DIST = 130;
const MOUSE_FORCE = 0.45;
const SPEED = 0.38;

function rand(min, max) { return Math.random() * (max - min) + min; }

const ParticleBackground = () => {
  const canvasRef = useRef(null);
  const frameRef  = useRef(null);
  const mouse     = useRef({ x: -9999, y: -9999 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let W = canvas.width  = window.innerWidth;
    let H = canvas.height = window.innerHeight;

    // Build particles
    const particles = Array.from({ length: PARTICLE_COUNT }, () => ({
      x:  rand(0, W),
      y:  rand(0, H),
      vx: rand(-SPEED, SPEED),
      vy: rand(-SPEED, SPEED),
      r:  rand(1.2, 2.8),
      pulse: rand(0, Math.PI * 2), // phase offset for glow pulse
    }));

    const onResize = () => {
      W = canvas.width  = window.innerWidth;
      H = canvas.height = window.innerHeight;
    };
    const onMouseMove = (e) => {
      mouse.current.x = e.clientX;
      mouse.current.y = e.clientY;
    };
    const onMouseLeave = () => {
      mouse.current.x = -9999;
      mouse.current.y = -9999;
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseleave', onMouseLeave);

    let tick = 0;

    function draw() {
      ctx.clearRect(0, 0, W, H);
      tick++;

      const mx = mouse.current.x;
      const my = mouse.current.y;

      // Update + draw particles
      for (const p of particles) {
        // Mouse repulsion
        const dxM = p.x - mx;
        const dyM = p.y - my;
        const distM = Math.sqrt(dxM * dxM + dyM * dyM);
        if (distM < MOUSE_FORCE_DIST && distM > 0) {
          const force = (MOUSE_FORCE_DIST - distM) / MOUSE_FORCE_DIST;
          p.vx += (dxM / distM) * force * MOUSE_FORCE;
          p.vy += (dyM / distM) * force * MOUSE_FORCE;
        }

        // Dampen velocity to max SPEED*2
        const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        if (speed > SPEED * 2.5) {
          p.vx = (p.vx / speed) * SPEED * 2.5;
          p.vy = (p.vy / speed) * SPEED * 2.5;
        }
        // Restore baseline drift when far from mouse
        if (distM > MOUSE_FORCE_DIST) {
          p.vx *= 0.995;
          p.vy *= 0.995;
          if (Math.abs(p.vx) < SPEED * 0.3) p.vx += rand(-0.01, 0.01);
          if (Math.abs(p.vy) < SPEED * 0.3) p.vy += rand(-0.01, 0.01);
        }

        p.x += p.vx;
        p.y += p.vy;

        // Wrap edges with small margin
        if (p.x < -20) p.x = W + 20;
        if (p.x > W + 20) p.x = -20;
        if (p.y < -20) p.y = H + 20;
        if (p.y > H + 20) p.y = -20;

        // Pulsing glow on particle
        const glow = 0.55 + 0.45 * Math.sin(tick * 0.025 + p.pulse);

        // Outer halo
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 7);
        grad.addColorStop(0, `rgba(180,210,255,${(glow * 0.18).toFixed(3)})`);
        grad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 7, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();

        // Core dot
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(210,230,255,${(glow * 0.72).toFixed(3)})`;
        ctx.fill();
      }

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i];
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DIST) {
            const alpha = (1 - dist / CONNECTION_DIST) * 0.22;
            // Slight blue tint on lines near the mouse
            const dxma = (a.x + b.x) * 0.5 - mx;
            const dyma = (a.y + b.y) * 0.5 - my;
            const distMid = Math.sqrt(dxma * dxma + dyma * dyma);
            const nearMouse = Math.max(0, 1 - distMid / 200);
            const r2 = Math.round(180 + nearMouse * 60);
            const g2 = Math.round(200 + nearMouse * 40);
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = `rgba(${r2},${g2},255,${alpha.toFixed(3)})`;
            ctx.lineWidth = 0.6 + nearMouse * 0.6;
            ctx.stroke();
          }
        }
      }

      // Draw occasional "shooting" highlight on the closest few lines to mouse
      frameRef.current = requestAnimationFrame(draw);
    }

    draw();

    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
        opacity: 0.75,
      }}
    />
  );
};

export default ParticleBackground;
